import React from 'react';
import { Li, P, Stack, Strong, Ul } from '@ovotech/element-v5';
import styled from 'styled-components';
import {
  COMPATIBLE_EVS_AND_CHARGERS_LINK_ALL,
  MONTHLY_PLAN_TERMS,
  PlanDetails,
} from '@monovo/ev-anytime';

const Heading = styled(P)`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 125% */
`;

const StyledList = styled(Ul)`
  /* This adds the indentation */
  padding-left: 30px;

  & > li {
    list-style-type: disc;

    &::before {
      display: none !important;
    }

    &::marker {
      font-size: 1.2em; /* Makes the bullet 20% larger than the text */
    }
  }
`;

const InlineLink = styled.a`
  color: inherit;
  font-weight: inherit;
  font-size: inherit;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

export const PayAsYouGo = ({ planDetails }: { planDetails: PlanDetails }) => {
  return (
    <Stack spaceBetween={3}>
      <Heading>Pay as you go</Heading>
      <P>Pay only for what you charge, no commitment.</P>
      <Heading>Greener driving, for less</Heading>
      <P>
        Smart charging lets us charge when the grid has more renewable energy,
        saving you money.
      </P>
      <Heading>Fixed rate freedom</Heading>
      <P>
        Pay as you go is a fixed rate of {planDetails.pricePerKwh} per kWh.
        Drive as much or as little as you want.
      </P>
      <Heading>No exit fees</Heading>
      <P>Cancel your plan anytime or easily switch to a monthly plan. </P>
    </Stack>
  );
};

export const VolvoPayAsYouGo = ({
  planDetails,
}: {
  planDetails: PlanDetails;
}) => {
  return (
    <Stack spaceBetween={3}>
      <Heading>Pay as you go</Heading>
      <P>Pay only for what you charge, no commitment.</P>
      <Heading>Greener driving, for less</Heading>
      <P>
        Smart charging lets us charge when the grid has more renewable energy,
        saving you money.
      </P>
      <Heading>Fixed rate freedom</Heading>
      <P>
        Pay as you go is a fixed rate of {planDetails.pricePerKwh} per kWh for
        Volvo Cars App users. Drive as much or as little as you want.
      </P>
      <Heading>No exit fees</Heading>
      <P>Cancel your plan anytime.</P>
    </Stack>
  );
};

export const PayMonthly = () => {
  return (
    <Stack spaceBetween={3}>
      <Heading>Pay Monthly</Heading>
      <P>Your mileage allowance is allocated on a monthly basis.</P>
      <StyledList>
        <Li>
          <P>The miles reset at the start of each month</P>
        </Li>
        <Li>
          <P>The miles don’t roll over to the next month</P>
        </Li>
      </StyledList>
      <P>
        If you go over your monthly allowance or Urgent Charge you’ll be charged
        at your usual home tariff rate
      </P>
      <P>Mileage may vary depending on your EV and driving style.</P>
    </Stack>
  );
};

export const PartnerPlanDetails = () => {
  const allowanceType = 'mileage';
  const unitLabel = 'Miles';

  return (
    <Stack spaceBetween={3}>
      <Heading>Pay monthly</Heading>
      <P>Your {allowanceType} allowance is allocated on a monthly basis.</P>
      <StyledList>
        <Li>
          <P>{unitLabel} reset at the start of each month</P>
        </Li>
        <Li>
          <P>{unitLabel} don’t roll over to the next month</P>
        </Li>
      </StyledList>
      <P>
        If you go over your monthly allowance or Urgent Charge you’ll be charged
        at your usual home tariff rate.
      </P>
      <P>
        {allowanceType.charAt(0).toUpperCase() + allowanceType.slice(1)} may
        vary depending on your EV and driving style.
      </P>
      <Heading>Cancelling a plan</Heading>
      <P>
        You can cancel your plan any time. If you cancel all your perks will
        also be cancelled.
      </P>
    </Stack>
  );
};

export const PublicCharging = (allowance: string) => {
  return (
    <Stack spaceBetween={3}>
      <Heading>£{allowance} public charging voucher</Heading>
      <P>
        You get £{allowance} in annual public charging credit to power your EV
        wherever you go.
      </P>
      <P>
        Our extensive network gives you access to the UK’s biggest charging
        networks. This includes <Strong>50,000+ UK</Strong> chargers and{' '}
        <Strong>400,000+ chargers</Strong> in Europe.{' '}
        <InlineLink href={MONTHLY_PLAN_TERMS} target="_blank">
          Terms
        </InlineLink>{' '}
        apply.
      </P>
    </Stack>
  );
};

export const MultipleCars = () => {
  return (
    <Stack spaceBetween={3}>
      <Heading>Multiple EVs</Heading>
      <P>
        If you’re planning to connect through a compatible EV charger, then
        you’re all set for multiple EVs.
      </P>
      <P>
        If you’re planning to connect through your EVs, you can only access our
        plans with a compatible EV.{' '}
        <InlineLink href={COMPATIBLE_EVS_AND_CHARGERS_LINK_ALL} target="_blank">
          Check our list
        </InlineLink>{' '}
        to make sure all your EVs are compatible.
      </P>
    </Stack>
  );
};

export const EvChargerCover = () => {
  return (
    <Stack spaceBetween={3}>
      <Heading>EV Charger Cover</Heading>
      <P>
        Applies to all monthly plans, sign up in the OVO Charge app. You're
        protected against:
      </P>
      <StyledList>
        <Li>
          <P>accidental damage</P>
        </Li>
        <Li>
          <P>fire</P>
        </Li>
        <Li>
          <P>faults</P>
        </Li>
        <Li>
          <P>vandalism</P>
        </Li>
        <Li>
          <P>theft</P>
        </Li>
      </StyledList>
      <P>
        If anything happens to your home EV charger, we'll aim to be with you
        within 24 hours.
      </P>
      <P>
        There is a £30 excess applied to each new claim made against the EV
        Charger Cover.
      </P>
      <P>
        EV Charger Cover will be provided by OVO Insurance Services Limited. You
        can get EV Charger Cover if:
      </P>
      <StyledList>
        <Li>
          <P>you own the EV charger</P>
        </Li>
        <Li>
          <P>your charger is not used to generate income</P>
        </Li>
        <Li>
          <P>your charger does not have an output over 22 kW</P>
        </Li>
        <Li>
          <P>
            your EV Charger is located on mainland Great Britain, the Isle of
            Wight, the Isle of Skye or Bute
          </P>
        </Li>
      </StyledList>
      <P>
        See{' '}
        <InlineLink href={MONTHLY_PLAN_TERMS} target="_blank">
          terms
        </InlineLink>{' '}
        for further details.
      </P>
    </Stack>
  );
};

export const ClearWattEvBatteryCheck = () => {
  return (
    <Stack spaceBetween={3}>
      <Heading>ClearWatt EV battery check</Heading>
      <P>
        Check the performance of your EV and the battery condition with
        ClearWatt, the UK’s independent authority on battery health. Free with
        1000 miles+ plans,{' '}
        <InlineLink href={MONTHLY_PLAN_TERMS} target="_blank">
          terms
        </InlineLink>{' '}
        apply.
      </P>
    </Stack>
  );
};

export const CancellingPlan = () => {
  return (
    <Stack spaceBetween={3}>
      <Heading>Cancelling a plan</Heading>
      <P>
        You can cancel your plan any time. If you cancel all your perks will
        also be cancelled.
      </P>
    </Stack>
  );
};

export const Perks = () => {
  return (
    <Stack spaceBetween={3}>
      <Heading>Perks</Heading>
      <P>
        Once you’ve activated your plan in the OVO Charge app, you can then use
        the app to redeem your perks.
      </P>
    </Stack>
  );
};

export const KwikFitClubDiscount = () => {
  return (
    <Stack spaceBetween={3}>
      <Heading>15% Kwik Fit Club discount</Heading>
      <P>
        Enjoy peace of mind with Kwik Fit Club, a monthly subscription service
        that includes repairs, tyre replacements and MOT. 15% off with 1000
        miles+ plans,{' '}
        <InlineLink href={MONTHLY_PLAN_TERMS} target="_blank">
          terms
        </InlineLink>{' '}
        apply.
      </P>
    </Stack>
  );
};
